import { Button } from '@optools/components/ui/button';
import { Command, CommandItem, CommandList } from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import {
  ActionType,
  EsniRuleActionInput,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { formatEnumValue } from '@optools/utils/format';
import { PlusCircle } from 'lucide-react';
import { useState } from 'react';

import { ActionTypeCard } from './action-type-card';
import { ViewingPoliciesActionTypeCard } from './viewing-policies-action-type-card';

export interface ActionTypeMultiSelectProps {
  disabled?: boolean;
  modal?: boolean;
  onActionSelectionChanged: (actions: EsniRuleActionInput[]) => void;
  selectedActions: EsniRuleActionInput[];
}

export function ActionTypeMultiSelect({
  disabled,
  modal = false,
  onActionSelectionChanged,
  selectedActions,
}: ActionTypeMultiSelectProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const availableActions = Object.values(ActionType).filter(
    (actionType) => !selectedActions.some((item) => item.actionType === actionType),
  );

  const handleActionTypeChanged = (actionType: ActionType) => {
    onActionSelectionChanged([...selectedActions, { actionType, metadata: {} }]);
    setPopoverOpen(false);
  };

  const handleOnRemoveClick = (actionType: ActionType) => {
    onActionSelectionChanged(selectedActions.filter((item) => item.actionType !== actionType));
  };

  const handleViewingPoliciesMetadataChanged = (values: string[]) => {
    const actionIndex = selectedActions.findIndex(
      (item) => item.actionType === ActionType.VIEWING_POLICY_LIST,
    );
    if (actionIndex < 0) {
      return;
    }

    selectedActions[actionIndex].metadata.hrefs = values;
    onActionSelectionChanged(selectedActions);
  };

  return (
    <div className="flex flex-col gap-4 overflow-hidden" data-testid="action-type-multiselect">
      <div className="flex flex-col gap-2">
        {selectedActions.map((item) => {
          const { actionType } = item;
          switch (actionType) {
            case ActionType.MLB_ALL_SINGLE_TEAM_ENTITLEMENTS:
              return (
                <ActionTypeCard
                  key={actionType}
                  actionType={actionType}
                  disabled={disabled}
                  onRemoveClicked={handleOnRemoveClick}
                />
              );
              break;
            case ActionType.VIEWING_POLICY_LIST: {
              const viewingPolicies = selectedActions.find(
                (item) => actionType === item.actionType,
              );
              return (
                <ViewingPoliciesActionTypeCard
                  key={actionType}
                  actionType={actionType}
                  disabled={disabled}
                  selectedViewingPolicies={viewingPolicies?.metadata.hrefs ?? []}
                  onRemoveClicked={handleOnRemoveClick}
                  onViewingPoliciesChanged={handleViewingPoliciesMetadataChanged}
                />
              );
            }
            default:
              return undefined;
          }
        })}
      </div>
      {availableActions.length > 0 && (
        <Popover modal={modal} open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button className="h-20 border-dashed" disabled={disabled} variant="outline">
              <div className="flex items-center gap-1">
                <PlusCircle />
                Action
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-(--radix-popover-trigger-width) p-0"
            side="bottom">
            <Command>
              <CommandList>
                {availableActions.map((actionType) => (
                  <CommandItem
                    key={actionType}
                    aria-label={actionType}
                    className="cursor-pointer"
                    value={actionType}
                    onSelect={() => {
                      handleActionTypeChanged(actionType);
                    }}>
                    {formatEnumValue(actionType)}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
